import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { AgoraService } from "../../services/agora.service";
import { AuthService } from "../../services/auth.service";
import { Stream } from "../../models/stream.model";
import { formatStreamElapsed, getCurrentUserId, getCurrentUserRang } from "../../helpers/utils";
import { FormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { ChatComponent } from "../chat/chat.component";
import { ConfirmModalComponent } from "../../components/confirm-modal/confirm-modal.component";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { Environment } from "../../../environments/environment.interface";
import { ViewersService, Viewer } from "../../services/viewers.service";
import { SecureStorageService } from "../../services/secure-storage.service";
import { ChatService } from "../../services/chat.service";
import { ShareModalComponent } from "../../components/share-modal/share-modal.component";
import { NotificationService } from "../../services/notification.service";

@Component({
  selector: "app-broadcaster",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ChatComponent,
    ConfirmModalComponent,
    ShareModalComponent,
  ],
  templateUrl: "./broadcaster.component.html",
  styleUrls: ["./broadcaster.component.scss"],
})
export class BroadcasterComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("localVideo") localVideo!: ElementRef;

  slug: string = "";
  stream: Stream | null = null;
  viewerCount: number = 0;
  currentUserId: any = "0";
  currentUserRole: string = "";
  showEndStreamModal: boolean = false;
  isLoading: boolean = false;

  // Stream control states
  isMuted: boolean = false;
  isVideoOff: boolean = false;
  isScreenSharing: boolean = false;
  isCameraOff: boolean = false;
  isBeautyEnabled: boolean = false;
  showSettings: boolean = false;

  isChatEnabled: boolean = true;
  showShareModal: boolean = false;
  shareUrl: string = "";
  shareTitle: string = "";

  // Liste des spectateurs
  viewers: Viewer[] = [];
  viewerNotifications: { id: number; name: string; timestamp: Date }[] = [];

  private subscriptions: Subscription[] = [];
  private liveElapsedTick = 0;
  private liveElapsedIntervalId: ReturnType<typeof setInterval> | undefined;
  private baseApiUrl = (environment as Environment).apiUrl;

  // Importé dynamiquement uniquement côté navigateur pour éviter l'exécution en SSR
  private agoraRTCModule: any | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private agoraService: AgoraService,
    private authService: AuthService,
    private http: HttpClient,
    private viewersService: ViewersService,
    private secureStorage: SecureStorageService,
    private chatService: ChatService,
    private notificationService: NotificationService
  ) {
    this.slug = this.route.snapshot.params["slug"];
    this.currentUserId = getCurrentUserId() || "0";
    this.currentUserRole = getCurrentUserRang();
  }

  private async getAgoraRTC(): Promise<any> {
    if (typeof window === "undefined") {
      throw new Error("AgoraRTC est disponible uniquement côté navigateur (SSR désactivé).");
    }
    if (this.agoraRTCModule) return this.agoraRTCModule;
    const mod: any = await import("agora-rtc-sdk-ng");
    this.agoraRTCModule = mod?.default ?? mod;
    return this.agoraRTCModule;
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.slug = params["slug"];
      if (this.slug) {
        this.loadStreamInfo();
        // Charger les spectateurs
        this.viewersService.loadViewers(this.slug);

        // S'abonner aux mises à jour des spectateurs
        this.subscriptions.push(
          this.viewersService.viewers$.subscribe((viewers) => {
            this.viewers = viewers;

            // Détecter les nouveaux spectateurs pour les notifications
            const newViewers = viewers.filter(
              (viewer) => !this.viewers.some((v) => v.id === viewer.id)
            );

            // Ajouter des notifications pour les nouveaux spectateurs
            newViewers.forEach((viewer) => {
              this.viewerNotifications.push({
                id: viewer.id,
                name: viewer.name,
                timestamp: new Date(),
              });

              // Supprimer la notification après 5 secondes
              setTimeout(() => {
                this.viewerNotifications = this.viewerNotifications.filter(
                  (notification) => notification.id !== viewer.id
                );
              }, 5000);
            });
          })
        );

        this.subscriptions.push(
          this.viewersService.totalViewers$.subscribe((count) => {
            this.viewerCount = count;
          })
        );
      }
    });

    this.chatService.getInitialChatState(this.slug).subscribe({
      next: (state) => {
        this.isChatEnabled = state;
        console.log("🎯 État initial du chat:", state);
      },
    });

    // Écouter les changements (au cas où)
    this.chatService.getChatState$().subscribe((state) => {
      console.log("🎯 Broadcast - État chat mis à jour:", state);
      this.isChatEnabled = state;
    });

    this.liveElapsedIntervalId = setInterval(() => {
      this.liveElapsedTick = Date.now();
    }, 1000);
  }

  /** Durée du live depuis started_at (comme sur mobile). */
  get liveElapsedDisplay(): string {
    void this.liveElapsedTick;
    return formatStreamElapsed(this.stream?.started_at);
  }

  toggleChat() {
    const newState = !this.isChatEnabled;
    console.log("🚀 Broadcaster change état chat:", newState);

    this.chatService.setChatState(this.slug, newState).subscribe({
      next: (response) => {
        console.log("✅ État chat mis à jour sur serveur");
        // L'événement Pusher sera émis par Laravel
      },
      error: (error) => {
        console.error("❌ Erreur mise à jour état chat:", error);
        this.isChatEnabled = !newState; // Annuler
      },
    });
  }

  private updateChatState() {
    // Ici, vous pouvez envoyer l'état du chat à votre backend
    // pour que tous les viewers soient notifiés en temps réel
    // Exemple :
    // this.streamService.updateChatState(this.slug, this.isChatEnabled).subscribe();

    console.log("Chat état:", this.isChatEnabled ? "Activé" : "Désactivé");
  }

  private loadStreamInfo(): void {
    this.currentUserId = getCurrentUserId() || "0";
    this.currentUserRole = getCurrentUserRang();
    if (this.currentUserRole !== "star") {
      this.router.navigate(["/streams"]);
      return;
    }

    this.agoraService.getStreamInfo(this.slug).subscribe({
      next: (stream) => {
        this.stream = stream;
        if (!this.canBroadcastCurrentStream(stream)) {
          console.error("Broadcast refusé: propriétaire du stream non reconnu", {
            streamUserId: stream?.user_id,
            currentUserId: this.currentUserId,
          });
          this.router.navigate(["/streams"]);
          return;
        }
        this.initializeBroadcaster();
      },
      error: (error) => {
        console.error(
          "Erreur lors du chargement des informations du stream:",
          error
        );
        this.router.navigate(["/streams"]);
      },
    });
  }

  private async initializeBroadcaster(): Promise<void> {
    try {
      console.log("Initialisation du broadcaster avec le slug:", this.slug);
      // Force le chargement du SDK uniquement côté navigateur (évite SSR)
      await this.getAgoraRTC();

      // Vérifier si l'utilisateur est connecté
      if (!this.currentUserId) {
        throw new Error("Utilisateur non connecté");
      }

      // Initialiser le broadcaster
      await this.agoraService.initializeBroadcaster(this.slug);

      // Afficher la vidéo locale
      const localVideoTrack = this.agoraService.getLocalVideoTrack();
      if (localVideoTrack) {
        localVideoTrack.play(this.localVideo.nativeElement);
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation du broadcaster:", error);
    }
  }

  private canBroadcastCurrentStream(stream: any): boolean {
    const ownerId =
      stream?.user_id ??
      stream?.star_id ??
      stream?.user?.id ??
      stream?.data?.user_id;
    if (ownerId == null || ownerId === "") return false;
    const currentId = this.currentUserId || getCurrentUserId();
    if (currentId == null || currentId === "" || currentId === "0") return false;
    return Number(ownerId) === Number(currentId);
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.agoraService.setMute(this.isMuted);
  }

  toggleVideo(): void {
    this.isVideoOff = !this.isVideoOff;
    this.agoraService.setVideoEnabled(!this.isVideoOff);
  }

  toggleScreenShare(): void {
    this.isScreenSharing = !this.isScreenSharing;
    if (this.isScreenSharing) {
      this.agoraService.startScreenShare();
    } else {
      this.agoraService.stopScreenShare();
    }
  }

  toggleCamera(): void {
    this.isCameraOff = !this.isCameraOff;
    this.agoraService.setCameraEnabled(!this.isCameraOff);
  }

  toggleBeauty(): void {
    this.isBeautyEnabled = !this.isBeautyEnabled;
    this.agoraService.setBeautyEffect(this.isBeautyEnabled);
  }

  toggleSettings(): void {
    this.showSettings = !this.showSettings;
    // Implémenter l'affichage des paramètres
  }

  showEndStreamConfirmation(): void {
    this.showEndStreamModal = true;
  }

  confirmEndStream(): void {
    this.showEndStreamModal = true;
  }

  endStream(): void {
    this.showEndStreamModal = false;
    this.isLoading = true;

    this.agoraService
      .stopStreaming()
      .then(() => {
        this.agoraService.endStream(this.slug).subscribe({
          next: () => {
            console.log("Stream terminé avec succès");
            this.isLoading = false;
            this.router.navigate(["/streams"]);
          },
          error: (error) => {
            console.error("Erreur lors de la fin du stream:", error);
            this.isLoading = false;
            this.notificationService.create(
              "Impossible de terminer le live sur le serveur.",
              "danger",
              4000
            );
            this.router.navigate(["/streams"]);
          },
        });
      })
      .catch((error) => {
        console.error("Erreur lors de l'arrêt du stream:", error);
        this.isLoading = false;
        // Rediriger quand même vers la page des streams
        this.router.navigate(["/streams"]);
      });
  }

  ngAfterViewInit() {
    console.log("Broadcaster view initialized");
  }

  openShareModal() {
    this.shareUrl = `${window.location.origin}/stream/${this.slug}/view`;
    this.shareTitle = this.stream?.title || "Regardez mon live !";
    this.showShareModal = true;
  }

  closeShareModal() {
    this.showShareModal = false;
  }

  ngOnDestroy(): void {
    if (this.liveElapsedIntervalId != null) {
      clearInterval(this.liveElapsedIntervalId);
    }
    // Se désabonner des mises à jour des spectateurs
    this.viewersService.unsubscribeFromViewersUpdates(this.slug);

    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.agoraService.stopStreaming();
  }
}
